import { css } from "@emotion/css";
import { Tooltip } from "@octopusdeploy/design-system-components";
import { GhostIcon, HourglassIcon, PauseIcon, CheckIcon } from "@octopusdeploy/design-system-icons";
import { themeTokens } from "@octopusdeploy/design-system-tokens";
import type { KubernetesLiveApplicationStatusCode, KubernetesLiveResourceHealthStatusCode, KubernetesLiveResourceSyncStatusCode } from "@octopusdeploy/octopus-server-client";
import * as React from "react";
import { DegradedIcon } from "~/areas/projects/components/Observability/LiveStatusIcons/DegradedIcon";
import { HealthyIcon } from "~/areas/projects/components/Observability/LiveStatusIcons/HealthyIcon";
import { InSyncIcon } from "~/areas/projects/components/Observability/LiveStatusIcons/InSyncIcon";
import { OutOfSyncIcon } from "~/areas/projects/components/Observability/LiveStatusIcons/OutOfSyncIcon";
import { ProgressingIcon } from "~/areas/projects/components/Observability/LiveStatusIcons/ProgressingIcon";
import { UnknownIcon } from "~/areas/projects/components/Observability/LiveStatusIcons/UnknownIcon";
import { UnavailableIcon } from "./LiveAppIcons";
const iconSize = 20;
type KubernetesLiveResourceIconStatusCode = KubernetesLiveResourceHealthStatusCode | KubernetesLiveResourceSyncStatusCode;
export function LiveStatusResourceIcon({ status }: {
    status: KubernetesLiveResourceIconStatusCode;
}) {
    switch (status) {
        case "Healthy":
            return (<Tooltip content={"Running normally"}>
                    <HealthyIcon />
                </Tooltip>);
        case "Available":
            return (<Tooltip content={"Running normally"}>
                    <span className={styles.greenIcon}>
                        <CheckIcon size={iconSize}/>
                    </span>
                </Tooltip>);
        case "Progressing":
            return (<Tooltip content={"Attempting to reach desired state"}>
                    <ProgressingIcon />
                </Tooltip>);
        case "Degraded":
            return (<Tooltip content={"Encountered issues"}>
                    <DegradedIcon />
                </Tooltip>);
        case "OutOfSync":
            return (<Tooltip content={"Different to what was deployed"}>
                    <OutOfSyncIcon />
                </Tooltip>);
        case "Missing":
            return (<Tooltip content={"Not found"}>
                    <GhostIcon size={iconSize}/>
                </Tooltip>);
        case "InSync":
            return (<Tooltip content={"Matches desired state"}>
                    <InSyncIcon />
                </Tooltip>);
        case "Suspended":
            return (<Tooltip content={"Suspended"}>
                    <PauseIcon size={iconSize}/>
                </Tooltip>);
        case "Unknown":
        default:
            return (<Tooltip content={"We don't have up-to-date information about the live status of this object"}>
                    <UnknownIcon />
                </Tooltip>);
    }
}
export function MachineStatusIcon({ status }: {
    status: KubernetesLiveApplicationStatusCode;
}) {
    switch (status) {
        case "Healthy":
            return (<Tooltip content={"All objects on this cluster are in a healthy state"}>
                    <HealthyIcon />
                </Tooltip>);
        case "Progressing":
            return (<Tooltip content={"Some objects on this cluster are attempting to reach their desired state"}>
                    <ProgressingIcon />
                </Tooltip>);
        case "Degraded":
            return (<Tooltip content={"Some objects on this cluster experienced errors after the deployment completed"}>
                    <DegradedIcon />
                </Tooltip>);
        case "OutOfSync":
            return (<Tooltip content={"Some objects on this cluster no longer match what you last deployed"}>
                    <OutOfSyncIcon />
                </Tooltip>);
        case "Missing":
            return (<Tooltip content={"Some objects on this cluster could not be found"}>
                    <GhostIcon size={iconSize}/>
                </Tooltip>);
        case "Waiting":
            return (<Tooltip content={"Deployment in progress"}>
                    <HourglassIcon size={iconSize}/>
                </Tooltip>);
        case "Unavailable":
            return <Tooltip content={"Live status is unavailable because your last deployment failed"}>{UnavailableIcon(iconSize)}</Tooltip>;
        case "Unknown":
        case "NotSupported":
        default:
            return (<Tooltip content={"Some objects on this cluster are in an unknown state"}>
                    <UnknownIcon />
                </Tooltip>);
    }
}
const styles = {
    greenIcon: css({
        color: themeTokens.color.icon.success,
    }),
};
